import { createContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { Account } from '../models/Account';
import axios from 'axios';
import { baseURL } from '../Constants';
import { analytics } from '../services/analytics';

// JWT Token payload interface
interface JWTPayload {
    exp: number;
    role?: string;
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'?: string;
    unique_name?: string;
    nameid?: string;
    name?: string;
    steamID?: string;
    playerName?: string;
    dateOfBirth?: string;
    age?: number;
    lft?: boolean;
    twitter?: string;
    [key: string]: unknown;
}

// Define the shape of AuthContext
interface AuthContextType {
    user: Account | null;
    token: string | null;
    isAdmin: boolean;
    isLoggedinAdmin: boolean;
    setToken: React.Dispatch<React.SetStateAction<string | null>>;
    adminLogin: (password: string) => Promise<boolean>;
    checkAdminStatus: () => boolean;
}

// Create context with default values
export const AuthContext = createContext<AuthContextType>({
    user: null,
    token: null,
    isAdmin: false,
    isLoggedinAdmin: false,
    setToken: () => { },
    adminLogin: async () => false,
    checkAdminStatus: () => false,
});

interface AuthProviderProps {
    children: React.ReactNode;
}

// Helper function to decode and validate token
const getValidToken = (storedToken: string | null): string | null => {
    if (!storedToken) return null;

    try {
        const decodedToken = jwtDecode<JWTPayload>(storedToken);
        
        const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();

        if (expirationTime > currentTime) {
            return storedToken;
        } else {
            localStorage.removeItem('token');
            localStorage.removeItem('adminToken');
            return null;
        }
    } catch (error) {
        console.error('Error decoding stored token:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('adminToken');
        return null;
    }
};

// Helper function to check if token has admin role
const isAdminToken = (token: string | null): boolean => {
    if (!token) return false;

    try {
        const decodedToken = jwtDecode<JWTPayload>(token);
        // Check if the token has an admin role claim
        // This depends on how your backend adds the admin role to the token
        return decodedToken.role === 'Admin-Perms' || 
               (decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'Admin');
    } catch (error) {
        console.error('Error checking admin status:', error);
        return false;
    }
};

const isAdminLoggedToken = (token: string | null): boolean => {
    if (!token) return false;

    try {
        const decodedToken = jwtDecode<JWTPayload>(token);
        // Check if the token has an admin role claim
        // This depends on how your backend adds the admin role to the token
        return decodedToken.role === 'Admin' || 
               (decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'Admin');
    } catch (error) {
        console.error('Error checking admin status:', error);
        return false;
    }
};

// Helper function to handle post-login setup
const handleUserLogin = (token: string | null, setUser: React.Dispatch<React.SetStateAction<Account | null>>) => {
    if (!token) {
        setUser(null);
        return;
    }

    try {
        const decodedToken = jwtDecode<JWTPayload>(token);
        
        const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();

        if (expirationTime > currentTime) {
            // Create a proper Account object from the token data
            // This ensures all expected properties are present
            const user: Account = {
                steamID: decodedToken.steamID || '',
                playerName: decodedToken.playerName || '',
                dateOfBirth: decodedToken.dateOfBirth || null, // Added type as string | null
                // Provide default values for missing properties
                name: decodedToken.name || '',
                age: decodedToken.age || 0,
                lft: decodedToken.lft || false,
                twitter: decodedToken.twitter || '',
                role: decodedToken.role || '',
                isAdmin: isAdminToken(token), // Add admin status to user object
                isLoggedinAdmin: isAdminLoggedToken(token) // Add admin status to user object
            };

            setUser(user);

            // Identify user in analytics
            if (user.steamID) {
                analytics.identifyUser(user.steamID, {
                    username: user.playerName || user.name,
                    role: user.isLoggedinAdmin ? 'admin' : (user.isAdmin ? 'admin-perms' : 'user'),
                    steamId: user.steamID
                });
            }
        } else {
            alert('Login expired, please log in again');
            setUser(null);
        }
    } catch (error) {
        console.error('Error decoding token:', error);
        setUser(null);
    }
};

// AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [token, setToken] = useState<string | null>(() => {
        // First try to get the admin token, then fall back to regular token
        const adminToken = localStorage.getItem('adminToken');
        if (adminToken) {
            const validAdminToken = getValidToken(adminToken);
            if (validAdminToken) return validAdminToken;
        }
        
        const storedToken = localStorage.getItem('token');
        return getValidToken(storedToken);
    });

    const [user, setUser] = useState<Account | null>(null);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [isLoggedinAdmin, setIsAdminLoggedIn] = useState<boolean>(false);

    // Admin login function
    const adminLogin = async (password: string): Promise<boolean> => {
        if (!user || !user.steamID) {
            console.error('No user is logged in');
            return false;
        }
        
        try {
            const response = await axios.post(`${baseURL}/Auth/adminLogin`, {
                steamID: user.steamID,
                password
            });
    
            if (response.data && response.data.token) {
                localStorage.setItem('adminToken', response.data.token);
                setToken(response.data.token);
                setIsAdminLoggedIn(true);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Admin login failed:', error);
            return false;
        }
    };

    // Check if the current user is an admin
    const checkAdminStatus = (): boolean => {
        return isAdmin;
    };

    const checkAdminLoggedInStatus = (): boolean => {
        return isLoggedinAdmin;
    };


    // Update token in localStorage and handle user login state
useEffect(() => {
    
    if (token) {
        // Check if this is an admin token
        const adminStatus = isAdminToken(token);
        setIsAdmin(adminStatus);

        const adminLoggedInStatus = isAdminLoggedToken(token);
        // Here's the bug - you're using adminStatus instead of adminLoggedInStatus
        setIsAdminLoggedIn(adminLoggedInStatus); // <-- FIX THIS LINE
        
        // Store in appropriate storage
        if (adminLoggedInStatus) {
            localStorage.setItem('adminToken', token);
        } else {
            localStorage.setItem('token', token);
        }
    } else {
        localStorage.removeItem('token');
        localStorage.removeItem('adminToken');
        setIsAdminLoggedIn(false);
        setIsAdmin(false);
    }
    
    handleUserLogin(token, setUser);
}, [token]);

    return (
        <AuthContext.Provider value={{ 
            setToken, 
            user, 
            token, 
            isAdmin, 
            isLoggedinAdmin,
            adminLogin, 
            checkAdminStatus 
        }}>
            {children}
        </AuthContext.Provider>
    );
};